import React, { useState, useEffect } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert, CircularProgress, Chip } from '@mui/material';
import axios from 'axios';
import config from '../config';

const renderSapinScoreChip = (value) => {
    const num = parseInt(value);
    if (isNaN(num) || num < 1) return value;

    let color = '#e0e0e0';
    let label = '';
    let textColor = 'white';

    // Color mapping based on score
    if (num === 1 || num === 2) {
        color = '#00B050'; // Dark green - Risque très faible
        label = 'Risque très faible';
    } else if (num === 3) {
        color = '#92D050'; // Light green - Risque faible
        label = 'Risque faible';
    } else if (num === 4 || num === 5) {
        color = '#FFFF00'; // Yellow - Risque faible
        label = 'Risque faible';
        textColor = 'black';
    } else if (num === 6 || num === 7) {
        color = '#FFC000'; // Orange - Risque modéré
        label = 'Risque modéré';
    } else if (num >= 8) {
        color = '#FF0000'; // Red - Risque élevé
        label = 'Risque élevé';
    }

    return (
        <Chip
            label={`${num}`}
            sx={{ backgroundColor: color, color: textColor, fontWeight: 'bold', height: 24 }}
        />
    );
};

const Compare = () => {
    const [tables, setTables] = useState([]);
    const [table1, setTable1] = useState('');
    const [table2, setTable2] = useState('');
    const [differences, setDifferences] = useState([]);
    const [commonColumns, setCommonColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchTables();
    }, []);

    const fetchTables = async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}/fournisseurs`);
            setTables(response.data);
        } catch (err) {
            console.error(err);
            setError('Failed to load tables');
        }
    };

    const handleCompare = async () => {
        if (!table1 || !table2) {
            setError('Please select two tables to compare');
            return;
        }

        setLoading(true);
        setError(null);
        setDifferences([]);

        try {
            const response = await axios.post(`${config.API_BASE_URL}/compare`, { table1, table2 });
            setDifferences(response.data.differences);
            setCommonColumns(response.data.commonColumns);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'Failed to compare tables');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Compare Tables</Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Table 1 (Base)</InputLabel>
                        <Select value={table1} label="Table 1 (Base)" onChange={(e) => setTable1(e.target.value)}>
                            {tables.map((t) => (
                                <MenuItem key={typeof t === 'string' ? t : t.name} value={typeof t === 'string' ? t : t.name}>
                                    {typeof t === 'string' ? t : t.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Typography>vs</Typography>

                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Table 2 (Target)</InputLabel>
                        <Select value={table2} label="Table 2 (Target)" onChange={(e) => setTable2(e.target.value)}>
                            {tables.map((t) => (
                                <MenuItem key={typeof t === 'string' ? t : t.name} value={typeof t === 'string' ? t : t.name}>
                                    {typeof t === 'string' ? t : t.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        onClick={handleCompare}
                        disabled={loading || !table1 || !table2}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Compare'}
                    </Button>
                </Box>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {differences.length > 0 && (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Status</TableCell>
                                {commonColumns.map(col => (
                                    <TableCell key={col}>{col}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {differences.map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        backgroundColor: row.diff_status.includes(table1) ? '#ffebee' : '#e8f5e9'
                                    }}
                                >
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {row.diff_status.includes(table1) ? 'Removed/Old' : 'Added/New'}
                                    </TableCell>
                                    {commonColumns.map(col => {
                                        const isScoreCol = col.toLowerCase().includes('score') || col.toLowerCase().includes('note');
                                        return (
                                            <TableCell key={col}>
                                                {isScoreCol ? renderSapinScoreChip(row[col]) : row[col]}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {differences.length === 0 && !loading && !error && table1 && table2 && (
                <Alert severity="info">No differences found or comparison not started.</Alert>
            )}
        </Box>
    );
};

export default Compare;
